import React, { useState, useRef, useMemo } from 'react'
import { SLEEP_STAGES_CONFIG } from '../../utils/sleepStaging'
import { IconClock, IconHeart } from './Icons'

/**
 * Gráfico Holístico de Arquitectura del Sueño (Estilo AASM / Amazon Halo).
 * Muestra los 4 niveles escalonados coloreados al 100%, curva de pulso sincronizada,
 * marcadores de ciclos ultradianos de 90 min y Sleep Score global.
 */
export function SleepHypnogram({ sleepData }) {
  const [hoveredEpoch, setHoveredEpoch] = useState(null)
  const [showTransitions, setShowTransitions] = useState(false)
  const svgRef = useRef(null)

  if (!sleepData || !sleepData.epochs || sleepData.epochs.length === 0) {
    return (
      <div style={{
        background: 'var(--card-bg)', border: '1px solid var(--border-soft)',
        borderRadius: 16, padding: '24px 16px', textAlign: 'center', marginBottom: 26,
        boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
      }}>
        <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', margin: 0 }}>
          Sin datos de descanso nocturno registrados
        </p>
        <p style={{ fontSize: 11, color: 'var(--text-faint)', margin: '4px 0 0' }}>
          Para registrar la arquitectura del sueño, usá el Polar H10 en la ventana de descanso nocturno en posición acostado.
        </p>
      </div>
    )
  }

  const {
    epochs, blocks, transitions, totalSleepFormatted, timeInBedFormatted,
    efficiencyPct, sleepScore, sleepScoreLabel, sleepScoreColor, cycles,
    nadirHr, avgSleepHr, bedTime, wakeTime, stages
  } = sleepData

  // Dimensiones del SVG
  const width = 820
  const height = 210
  const paddingLeft = 68
  const paddingRight = 20
  const paddingTop = 20
  const paddingBottom = 42
  const chartW = width - paddingLeft - paddingRight
  const chartH = height - paddingTop - paddingBottom

  // Mapeo vertical de niveles: Level 4 (Awake) arriba, Level 1 (Deep) abajo
  const levelY = {
    4: paddingTop + chartH * 0.08, // Awake
    3: paddingTop + chartH * 0.38, // REM
    2: paddingTop + chartH * 0.68, // Light
    1: paddingTop + chartH * 0.95, // Deep
  }

  // Escala horizontal X (en base a la cantidad de épocas)
  const n = epochs.length
  const getX = (idx) => paddingLeft + (idx / Math.max(1, n - 1)) * chartW

  // Rango de pulso para la curva cardíaca de fondo
  const hrs = epochs.map(e => e.hr).filter(Boolean)
  const minHr = Math.min(...hrs, nadirHr || 45) - 2
  const maxHr = Math.max(...hrs, avgSleepHr ? avgSleepHr + 15 : 90) + 4
  const getHrY = (hr) => {
    const norm = (hr - minHr) / (maxHr - minHr)
    return paddingTop + chartH - norm * chartH
  }

  // Trazado de la curva de pulso de fondo sincronizada
  const hrPathD = useMemo(() => {
    if (epochs.length < 2) return ''
    return epochs.reduce((acc, ep, i) => {
      const x = getX(ep.idx)
      const y = getHrY(ep.hr)
      return i === 0 ? `M ${x} ${y}` : `${acc} L ${x} ${y}`
    }, '')
  }, [epochs, minHr, maxHr])

  // Manejo de scrubber / hover interactivo
  const handleMouseMove = (e) => {
    if (!svgRef.current) return
    const rect = svgRef.current.getBoundingClientRect()
    const relX = e.clientX - rect.left
    const clampedX = Math.max(paddingLeft, Math.min(rect.width - paddingRight, relX))
    const ratio = (clampedX - paddingLeft) / (rect.width - paddingLeft - paddingRight)
    const idx = Math.min(n - 1, Math.max(0, Math.floor(ratio * n)))
    setHoveredEpoch(epochs[idx])
  }

  const handleMouseLeave = () => {
    setHoveredEpoch(null)
  }

  return (
    <div style={{
      background: 'var(--card-bg)', border: '1px solid var(--border-soft)',
      borderRadius: 16, padding: '16px 18px', marginBottom: 26,
      boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
    }}>
      {/* ── 1. Encabezado Holístico con Sleep Score y Métricas Clave ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 9.5, fontWeight: 900, background: 'var(--chip-bg)', color: 'var(--text)', padding: '2px 7px', borderRadius: 6, fontVariantNumeric: 'tabular-nums' }}>
              06
            </span>
            <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
              Arquitectura del Sueño (Hipnograma)
            </span>
            {/* Píldora de Sleep Score Global */}
            <span style={{
              fontSize: 10, fontWeight: 900, padding: '2px 8px', borderRadius: 12,
              background: `${sleepScoreColor}18`, color: sleepScoreColor, border: `1px solid ${sleepScoreColor}35`,
              letterSpacing: 0.3
            }}>
              Calidad {sleepScore}/100 · {sleepScoreLabel}
            </span>
          </div>
          <span style={{ fontSize: 11, color: 'var(--text-faint)', display: 'block', marginTop: 3 }}>
            De {bedTime} a {wakeTime} (hora local) · {cycles?.length || 4} ciclos ultradianos registrados
          </span>
        </div>

        <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ textAlign: 'right' }}>
            <span style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', display: 'block' }}>Tiempo dormido</span>
            <span style={{ fontSize: 16, fontWeight: 900, color: '#5856d6', fontVariantNumeric: 'tabular-nums' }}>
              {totalSleepFormatted}
            </span>
          </div>
          <div style={{ width: 1, height: 22, background: 'var(--border)' }} />
          <div style={{ textAlign: 'right' }}>
            <span style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', display: 'block' }}>Eficiencia</span>
            <span style={{ fontSize: 16, fontWeight: 900, color: efficiencyPct >= 85 ? '#30d158' : '#007aff', fontVariantNumeric: 'tabular-nums' }}>
              {efficiencyPct}%
            </span>
          </div>
          <div style={{ width: 1, height: 22, background: 'var(--border)' }} />
          <div style={{ textAlign: 'right' }}>
            <span style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', display: 'block' }}>Nadir FC</span>
            <span style={{ fontSize: 16, fontWeight: 900, color: '#007aff', fontVariantNumeric: 'tabular-nums' }}>
              {nadirHr} bpm
            </span>
          </div>
        </div>
      </div>

      {/* ── 2. Hipnograma Escalonado Interactivo con Curva de Pulso y Niveles ── */}
      <div style={{ position: 'relative', width: '100%', overflow: 'hidden' }}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${width} ${height}`}
          style={{ width: '100%', height: 'auto', display: 'block', touchAction: 'none', cursor: 'crosshair' }}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          onTouchMove={(e) => {
            if (e.touches[0]) handleMouseMove(e.touches[0])
          }}
          onTouchEnd={handleMouseLeave}
        >
          <defs>
            {/* Degradados sutiles para los carriles de cada nivel */}
            <linearGradient id="hrPulseGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#007aff" stopOpacity="0.45" />
              <stop offset="100%" stopColor="#007aff" stopOpacity="0.05" />
            </linearGradient>
          </defs>

          {/* Carriles guía coloreados de los 4 niveles con etiquetas */}
          {Object.values(SLEEP_STAGES_CONFIG).map(s => {
            const y = levelY[s.level]
            const bandHeight = chartH * 0.23
            return (
              <g key={s.key}>
                {/* Carril de fondo tenue */}
                <rect
                  x={paddingLeft}
                  y={y - bandHeight / 2}
                  width={chartW}
                  height={bandHeight}
                  fill={s.color}
                  opacity="0.045"
                />
                <line
                  x1={paddingLeft}
                  y1={y}
                  x2={width - paddingRight}
                  y2={y}
                  stroke={s.color}
                  strokeDasharray="3 4"
                  strokeWidth="1"
                  opacity="0.3"
                />
                <text
                  x={paddingLeft - 8}
                  y={y + 4}
                  textAnchor="end"
                  fill={s.color}
                  fontSize="11"
                  fontWeight="800"
                  fontFamily="inherit"
                >
                  {s.name}
                </text>
              </g>
            )
          })}

          {/* Curva tenue de pulso sincronizada en el fondo del hipnograma */}
          {hrPathD && (
            <path
              d={hrPathD}
              fill="none"
              stroke="#007aff"
              strokeWidth="1.2"
              strokeOpacity="0.28"
              strokeDasharray="2 3"
            />
          )}

          {/* Bloques de sueño: barras completas con color sólido para toda la altura del nivel */}
          {blocks.map((b, i) => {
            const startIdx = epochs.findIndex(e => e.time === b.start)
            const endIdx = startIdx + Math.max(1, Math.round(b.durationMin))
            const x1 = getX(Math.max(0, startIdx))
            const x2 = getX(Math.min(n - 1, endIdx))
            const w = Math.max(2, x2 - x1)
            const barH = 22
            const y = levelY[b.level] - barH / 2
            return (
              <g key={i}>
                <rect
                  x={x1}
                  y={y}
                  width={w}
                  height={barH}
                  rx="5"
                  fill={b.color}
                  opacity="0.88"
                />
                <rect
                  x={x1}
                  y={y}
                  width={w}
                  height={barH}
                  rx="5"
                  fill="none"
                  stroke={b.color}
                  strokeWidth="1.2"
                />
              </g>
            )
          })}

          {/* Conectores verticales entre transiciones de nivel */}
          {blocks.map((b, i) => {
            if (i >= blocks.length - 1) return null
            const nextB = blocks[i + 1]
            const startIdxNext = epochs.findIndex(e => e.time === nextB.start)
            const transX = getX(Math.max(0, startIdxNext))
            const y1 = levelY[b.level]
            const y2 = levelY[nextB.level]
            return (
              <line
                key={`trans_${i}`}
                x1={transX}
                y1={y1}
                x2={transX}
                y2={y2}
                stroke={nextB.color}
                strokeWidth="2.5"
                strokeLinecap="round"
                opacity="0.85"
              />
            )
          })}

          {/* Marcadores de Ciclos Ultradianos de 90 min en la parte inferior */}
          {cycles && cycles.map(c => {
            const cStartIdx = epochs.findIndex(e => e.time >= c.start)
            const cEndIdx = epochs.findIndex(e => e.time >= c.end)
            const cx1 = getX(Math.max(0, cStartIdx >= 0 ? cStartIdx : 0))
            const cx2 = getX(Math.min(n - 1, cEndIdx >= 0 ? cEndIdx : n - 1))
            const cw = Math.max(20, cx2 - cx1)
            const cy = height - 20
            return (
              <g key={`cycle_${c.num}`}>
                <line x1={cx1} y1={cy} x2={cx2} y2={cy} stroke="var(--border)" strokeWidth="1.5" />
                <line x1={cx1} y1={cy - 3} x2={cx1} y2={cy + 3} stroke="var(--border)" strokeWidth="1.5" />
                <line x1={cx2} y1={cy - 3} x2={cx2} y2={cy + 3} stroke="var(--border)" strokeWidth="1.5" />
                <text
                  x={cx1 + cw / 2}
                  y={cy - 4}
                  textAnchor="middle"
                  fill="var(--text-faint)"
                  fontSize="8.5"
                  fontWeight="700"
                  fontFamily="inherit"
                >
                  Ciclo {c.num}
                </text>
              </g>
            )
          })}

          {/* Eje de Horas en la parte inferior */}
          {epochs.filter((_, i) => i === 0 || i === Math.floor(n * 0.25) || i === Math.floor(n * 0.5) || i === Math.floor(n * 0.75) || i === n - 1).map((e, i) => (
            <text
              key={i}
              x={getX(e.idx)}
              y={height - 4}
              textAnchor="middle"
              fill="var(--text-faint)"
              fontSize="9.5"
              fontWeight="600"
              fontFamily="inherit"
            >
              {e.timeLabel}
            </text>
          ))}

          {/* Línea de cursor vertical del hover */}
          {hoveredEpoch && (
            <g>
              <line
                x1={getX(hoveredEpoch.idx)}
                y1={paddingTop}
                x2={getX(hoveredEpoch.idx)}
                y2={height - paddingBottom}
                stroke={hoveredEpoch.color}
                strokeWidth="1.5"
                strokeDasharray="2 2"
              />
              <circle
                cx={getX(hoveredEpoch.idx)}
                cy={levelY[hoveredEpoch.level]}
                r="5"
                fill={hoveredEpoch.color}
                stroke="#fff"
                strokeWidth="2"
              />
            </g>
          )}
        </svg>

        {/* Tooltip interactivo flotante enriquecido estilo Apple Health / Halo */}
        {hoveredEpoch && (
          <div style={{
            position: 'absolute',
            top: 6,
            left: `clamp(12px, ${((hoveredEpoch.idx / n) * 100)}%, calc(100% - 220px))`,
            transform: 'translateX(-50%)',
            background: 'var(--surface)',
            border: `1.5px solid ${hoveredEpoch.color}`,
            borderRadius: 12,
            padding: '8px 12px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
            pointerEvents: 'none',
            zIndex: 10,
            minWidth: 170,
            animation: 'fadeIn 0.15s ease'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
              <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-faint)' }}>
                {hoveredEpoch.timeLabel}
              </span>
              <span style={{
                fontSize: 9.5, fontWeight: 800, color: hoveredEpoch.color,
                background: `${hoveredEpoch.color}18`, padding: '2px 6px', borderRadius: 4
              }}>
                {hoveredEpoch.label}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 900, color: 'var(--text)' }}>
                {hoveredEpoch.hr} bpm
              </span>
              <span style={{ fontSize: 10.5, color: '#64d2ff', fontWeight: 800 }}>
                HRV {hoveredEpoch.rmssd} ms
              </span>
            </div>
            <p style={{ fontSize: 9.5, color: 'var(--text-muted)', margin: '3px 0 0', lineHeight: 1.3 }}>
              {hoveredEpoch.desc}
            </p>
          </div>
        )}
      </div>

      {/* ── 3. Fichas de Porcentaje y Duración por Fase (Estilo Amazon Halo) ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8, marginTop: 14 }}>
        {/* Profundo */}
        <div style={{
          background: 'var(--field-bg)', border: 'none',
          borderRadius: 10, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 3
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 10.5, fontWeight: 800, color: stages.deep.cfg.color }}>Profundo</span>
            <span style={{ fontSize: 9, fontWeight: 800, color: '#30d158' }}>{stages.deep.status}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
            <span style={{ fontSize: 15, fontWeight: 900, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
              {stages.deep.pct}%
            </span>
            <span style={{ fontSize: 10, color: 'var(--text-faint)', fontVariantNumeric: 'tabular-nums' }}>
              {stages.deep.formatted}
            </span>
          </div>
          <div style={{ height: 4, background: 'var(--track)', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ width: `${Math.min(100, (stages.deep.pct / 25) * 100)}%`, height: '100%', background: stages.deep.cfg.color }} />
          </div>
          <span style={{ fontSize: 9, color: 'var(--text-faint)' }}>Meta: 15–25%</span>
        </div>

        {/* REM */}
        <div style={{
          background: 'var(--field-bg)', border: 'none',
          borderRadius: 10, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 3
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 10.5, fontWeight: 800, color: stages.rem.cfg.color }}>REM</span>
            <span style={{ fontSize: 9, fontWeight: 800, color: '#30d158' }}>{stages.rem.status}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
            <span style={{ fontSize: 15, fontWeight: 900, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
              {stages.rem.pct}%
            </span>
            <span style={{ fontSize: 10, color: 'var(--text-faint)', fontVariantNumeric: 'tabular-nums' }}>
              {stages.rem.formatted}
            </span>
          </div>
          <div style={{ height: 4, background: 'var(--track)', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ width: `${Math.min(100, (stages.rem.pct / 25) * 100)}%`, height: '100%', background: stages.rem.cfg.color }} />
          </div>
          <span style={{ fontSize: 9, color: 'var(--text-faint)' }}>Meta: 20–25%</span>
        </div>

        {/* Ligero */}
        <div style={{
          background: 'var(--field-bg)', border: 'none',
          borderRadius: 10, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 3
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 10.5, fontWeight: 800, color: stages.light.cfg.color }}>Ligero</span>
            <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-faint)' }}>Base</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
            <span style={{ fontSize: 15, fontWeight: 900, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
              {stages.light.pct}%
            </span>
            <span style={{ fontSize: 10, color: 'var(--text-faint)', fontVariantNumeric: 'tabular-nums' }}>
              {stages.light.formatted}
            </span>
          </div>
          <div style={{ height: 4, background: 'var(--track)', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ width: `${Math.min(100, (stages.light.pct / 55) * 100)}%`, height: '100%', background: stages.light.cfg.color }} />
          </div>
          <span style={{ fontSize: 9, color: 'var(--text-faint)' }}>Meta: 45–55%</span>
        </div>

        {/* Despierto */}
        <div style={{
          background: 'var(--field-bg)', border: 'none',
          borderRadius: 10, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 3
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 10.5, fontWeight: 800, color: stages.awake.cfg.color }}>Despierto</span>
            <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-faint)' }}>{stages.awake.count} veces</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
            <span style={{ fontSize: 15, fontWeight: 900, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
              {stages.awake.pct}%
            </span>
            <span style={{ fontSize: 10, color: 'var(--text-faint)', fontVariantNumeric: 'tabular-nums' }}>
              {stages.awake.formatted}
            </span>
          </div>
          <div style={{ height: 4, background: 'var(--track)', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ width: `${Math.min(100, (stages.awake.pct / 15) * 100)}%`, height: '100%', background: stages.awake.cfg.color }} />
          </div>
          <span style={{ fontSize: 9, color: 'var(--text-faint)' }}>Meta: &lt; 10%</span>
        </div>
      </div>

      {/* ── 4. Registro Prolijo de Transiciones de Fase ── */}
      <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid var(--border-soft)' }}>
        <button
          type="button"
          onClick={() => setShowTransitions(o => !o)}
          style={{
            background: 'none', border: 'none', color: '#007aff',
            fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', gap: 5, padding: 0, textDecoration: 'underline'
          }}
        >
          <span>{showTransitions ? '▲ Ocultar transiciones' : '▼ Ver cronología exacta de transiciones de fase'}</span>
          <span style={{ fontSize: 9.5, color: 'var(--text-faint)', textDecoration: 'none' }}>
            ({transitions.length} cambios de estado)
          </span>
        </button>

        {showTransitions && (
          <div style={{
            marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6,
            maxHeight: 220, overflowY: 'auto', paddingRight: 4, animation: 'fadeIn 0.2s ease'
          }}>
            {transitions.map((tr, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '5px 8px', borderRadius: 6, background: 'var(--field-bg)',
                  borderLeft: `3px solid ${tr.toColor}`
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                    {tr.time}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--text)' }}>
                    Pasaste a <strong style={{ color: tr.toColor }}>{tr.toLabel}</strong>
                  </span>
                </div>
                <span style={{ fontSize: 10, color: 'var(--text-faint)' }}>
                  (venías de {tr.fromLabel} · {tr.durationBeforeMin} min)
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
